<template>
  <div class="formula-calculator sensor-mode">
    <!-- Header -->
    <div class="calculator-header" @click="toggleExpanded">
      <div class="header-left">
        <span class="material-symbols-outlined header-icon">tune</span>
        <div class="header-info">
          <h4 class="header-title">Formule de calcul</h4>
          <p class="header-preview" v-if="!expanded && rules.length > 0">
            {{ rulesPreviewText }}
          </p>
          <p class="header-preview empty" v-else-if="!expanded">Aucune transformation définie</p>
        </div>
      </div>
      <div class="header-actions">
        <span class="formula-badge" v-if="rules.length > 0">
          {{ rules.length }} règle{{ rules.length !== 1 ? 's' : '' }}
        </span>
        <button type="button" class="expand-btn" :class="{ expanded }">
          <span class="material-symbols-outlined">expand_more</span>
        </button>
      </div>
    </div>

    <!-- Expanded Content -->
    <transition name="slide-expand">
      <div class="calculator-body" v-if="expanded">

        <!-- Category Tabs -->
        <div class="category-tabs">
          <button
              v-for="cat in categories"
              :key="cat.id"
              type="button"
              class="category-tab"
              :class="{ active: activeCategory === cat.id }"
              @click="activeCategory = cat.id"
          >
            <span class="material-symbols-outlined">{{ cat.icon }}</span>
            <span class="tab-label">{{ cat.label }}</span>
            <span class="tab-count" v-if="getCategoryCount(cat.id) > 0">
              {{ getCategoryCount(cat.id) }}
            </span>
          </button>
        </div>

        <!-- Rules List -->
        <div class="rules-list">
          <div class="rules-header">
            <span class="material-symbols-outlined">list</span>
            <h4>Règles actives</h4>
            <div class="rules-actions" v-if="rules.length > 0">
              <button type="button" class="btn-icon" @click="toggleAllRules" :title="allEnabled ? 'Désactiver tout' : 'Activer tout'">
                <span class="material-symbols-outlined">{{ allEnabled ? 'toggle_on' : 'toggle_off' }}</span>
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div class="empty-rules" v-if="rules.length === 0">
            <span class="material-symbols-outlined">rule</span>
            <p>Aucune règle définie</p>
            <span class="hint">Sélectionnez une catégorie et ajoutez des règles</span>
          </div>

          <!-- Rules -->
          <div class="rules-container" v-if="rules.length > 0">
            <div
                class="rule-card"
                v-for="(rule, index) in rules"
                :key="rule.id"
                :class="[rule.category, { disabled: !rule.enabled }]"
            >
              <div class="rule-drag" title="Glisser pour réordonner">
                <span class="material-symbols-outlined">drag_indicator</span>
              </div>

              <button
                  type="button"
                  class="rule-toggle"
                  @click="rule.enabled = !rule.enabled; emitUpdate()"
                  :title="rule.enabled ? 'Désactiver' : 'Activer'"
              >
                <span class="material-symbols-outlined">
                  {{ rule.enabled ? 'check_circle' : 'radio_button_unchecked' }}
                </span>
              </button>

              <div class="rule-content">
                <span class="rule-tag" :class="rule.category">
                  <span class="material-symbols-outlined">{{ getRuleIcon(rule) }}</span>
                  {{ getRuleLabel(rule) }}
                </span>
                <span class="rule-description">{{ getRuleDescription(rule) }}</span>
              </div>

              <button type="button" class="rule-delete" @click="removeRule(index)" title="Supprimer">
                <span class="material-symbols-outlined">close</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Add Rule Panel -->
        <div class="add-panel">

          <!-- CONDITIONS -->
          <div class="panel-section" v-if="activeCategory === 'conditions'">
            <div class="section-intro">
              <span class="material-symbols-outlined">info</span>
              <p>Les conditions contrôlent quand les règles suivantes s'appliquent ou si la donnée est affichée.</p>
            </div>

            <div class="options-grid">
              <div
                  v-for="cond in conditionTypes"
                  :key="cond.type"
                  class="option-card"
                  :class="{ selected: selectedCondition === cond.type }"
                  @click="selectCondition(cond)"
              >
                <span class="material-symbols-outlined option-icon">{{ cond.icon }}</span>
                <div class="option-info">
                  <h5>{{ cond.label }}</h5>
                  <p>{{ cond.description }}</p>
                </div>
              </div>
            </div>

            <!-- Condition Builder -->
            <transition name="slide-fade">
              <div class="builder-panel" v-if="selectedCondition">
                <div class="builder-grid">
                  <!-- Source (for value conditions) -->
                  <div class="builder-field" v-if="needsSource">
                    <label>Source de la valeur</label>
                    <div class="button-group">
                      <button
                          type="button"
                          v-for="src in valueSources"
                          :key="src.value"
                          class="group-btn"
                          :class="{ active: conditionBuilder.source === src.value }"
                          @click="conditionBuilder.source = src.value"
                      >
                        {{ src.label }}
                      </button>
                    </div>
                  </div>

                  <!-- Comparison Operator -->
                  <div class="builder-field" v-if="needsComparison">
                    <label>Opérateur</label>
                    <div class="button-group">
                      <button
                          type="button"
                          v-for="op in conditionComparisonOperators"
                          :key="op.value"
                          class="group-btn mono"
                          :class="{ active: conditionBuilder.operator === op.value }"
                          @click="conditionBuilder.operator = op.value"
                      >
                        {{ op.display }}
                      </button>
                    </div>
                  </div>

                  <!-- Value -->
                  <div class="builder-field" v-if="needsTemporalValue">
                    <label>{{ temporalValueLabel }}</label>
                    <input
                        :type="temporalInputType"
                        v-model="conditionBuilder.value"
                        class="builder-input"
                        :step="selectedCondition === 'dateCompare' ? null : 60"
                    />
                    <small>Comparaison avec la date et l'heure de la mesure affichée.</small>
                  </div>

                  <div class="builder-field" v-if="needsValue">
                    <label>Valeur</label>
                    <input
                        type="number"
                        v-model="conditionBuilder.value"
                        class="builder-input"
                        placeholder="0"
                        step="any"
                    />
                  </div>

                  <!-- Range Values -->
                  <template v-if="needsRange">
                    <div class="builder-field">
                      <label>Valeur min</label>
                      <input
                          type="number"
                          v-model="conditionBuilder.min"
                          class="builder-input"
                          placeholder="0"
                          step="any"
                      />
                    </div>
                    <div class="builder-field">
                      <label>Valeur max</label>
                      <input
                          type="number"
                          v-model="conditionBuilder.max"
                          class="builder-input"
                          placeholder="100"
                          step="any"
                      />
                    </div>
                  </template>

                  <!-- Time fields -->
                  <template v-if="needsTime">
                    <div class="builder-field">
                      <label>Heure début</label>
                      <input
                          type="time"
                          v-model="conditionBuilder.startTime"
                          class="builder-input"
                      />
                    </div>
                    <div class="builder-field">
                      <label>Heure fin</label>
                      <input
                          type="time"
                          v-model="conditionBuilder.endTime"
                          class="builder-input"
                      />
                    </div>
                  </template>

                  <!-- Day selection -->
                  <div class="builder-field full-width" v-if="needsDays">
                    <label>Jours</label>
                    <div class="days-selector">
                      <button
                          type="button"
                          v-for="day in weekDays"
                          :key="day.value"
                          class="day-btn"
                          :class="{ active: conditionBuilder.days && conditionBuilder.days.includes(day.value) }"
                          @click="toggleDay(day.value)"
                      >
                        {{ day.short }}
                      </button>
                    </div>
                  </div>

                  <!-- Show Only Toggle -->
                  <div class="builder-field full-width" v-if="canBeShowOnly">
                    <label class="toggle-label">
                      <input type="checkbox" v-model="conditionBuilder.showOnly" class="toggle-input" />
                      <span class="toggle-switch"></span>
                      <span class="toggle-text">
                        <strong>Condition d'affichage</strong>
                        <small>Masque la donnée si la condition est fausse</small>
                      </span>
                    </label>
                  </div>
                </div>

                <button type="button" class="btn btn-add" @click="addCondition" :disabled="!canAddCondition">
                  <span class="material-symbols-outlined">add</span>
                  Ajouter la condition
                </button>
              </div>
            </transition>
          </div>

          <!-- OPERATIONS -->
          <div class="panel-section" v-if="activeCategory === 'operations'">
            <div class="section-intro">
              <span class="material-symbols-outlined">info</span>
              <p>Les opérations transforment la valeur du capteur. Elles s'appliquent dans l'ordre.</p>
            </div>

            <div class="options-grid">
              <div
                  v-for="op in operationTypes"
                  :key="op.type"
                  class="option-card"
                  :class="[op.colorClass, { selected: selectedOperation === op.type }]"
                  @click="selectOperation(op)"
              >
                <span class="material-symbols-outlined option-icon">{{ op.icon }}</span>
                <div class="option-info">
                  <h5>{{ op.label }}</h5>
                  <p>{{ op.description }}</p>
                </div>
              </div>
            </div>

            <!-- Operation Builder -->
            <transition name="slide-fade">
              <div class="builder-panel" v-if="selectedOperation">
                <div class="builder-grid">
                  <!-- Value input for most operations -->
                  <div class="builder-field" v-if="operationNeedsValue">
                    <label>{{ getOperationValueLabel() }}</label>
                    <input
                        type="number"
                        v-model="operationBuilder.value"
                        class="builder-input"
                        :placeholder="getOperationPlaceholder()"
                        step="any"
                    />
                  </div>

                  <!-- Precision for round -->
                  <div class="builder-field" v-if="selectedOperation === 'round'">
                    <label>Décimales</label>
                    <input
                        type="number"
                        v-model="operationBuilder.precision"
                        class="builder-input"
                        placeholder="2"
                        min="0"
                        max="10"
                    />
                  </div>

                  <!-- Min/Max for clamp -->
                  <template v-if="selectedOperation === 'clamp'">
                    <div class="builder-field">
                      <label>Minimum</label>
                      <input
                          type="number"
                          v-model="operationBuilder.min"
                          class="builder-input"
                          placeholder="0"
                          step="any"
                      />
                    </div>
                    <div class="builder-field">
                      <label>Maximum</label>
                      <input
                          type="number"
                          v-model="operationBuilder.max"
                          class="builder-input"
                          placeholder="100"
                          step="any"
                      />
                    </div>
                  </template>

                  <!-- Map range -->
                  <template v-if="selectedOperation === 'map'">
                    <div class="builder-field">
                      <label>Entrée min</label>
                      <input type="number" v-model="operationBuilder.inMin" class="builder-input" placeholder="0" step="any" />
                    </div>
                    <div class="builder-field">
                      <label>Entrée max</label>
                      <input type="number" v-model="operationBuilder.inMax" class="builder-input" placeholder="100" step="any" />
                    </div>
                    <div class="builder-field">
                      <label>Sortie min</label>
                      <input type="number" v-model="operationBuilder.outMin" class="builder-input" placeholder="0" step="any" />
                    </div>
                    <div class="builder-field">
                      <label>Sortie max</label>
                      <input type="number" v-model="operationBuilder.outMax" class="builder-input" placeholder="1" step="any" />
                    </div>
                  </template>

                  <!-- Window size for moving average -->
                  <div class="builder-field" v-if="selectedOperation === 'movingAvg'">
                    <label>Taille de la fenêtre</label>
                    <input
                        type="number"
                        v-model="operationBuilder.window"
                        class="builder-input"
                        placeholder="5"
                        min="2"
                        max="100"
                    />
                  </div>

                  <!-- Threshold for deadband -->
                  <div class="builder-field" v-if="selectedOperation === 'deadband'">
                    <label>Seuil (± valeur)</label>
                    <input
                        type="number"
                        v-model="operationBuilder.threshold"
                        class="builder-input"
                        placeholder="0.5"
                        step="any"
                    />
                  </div>
                </div>

                <button type="button" class="btn btn-add" @click="addOperation" :disabled="!canAddOperation">
                  <span class="material-symbols-outlined">add</span>
                  Ajouter l'opération
                </button>
              </div>
            </transition>
          </div>

          <!-- FORMATTING -->
          <div class="panel-section" v-if="activeCategory === 'formatting'">
            <div class="section-intro">
              <span class="material-symbols-outlined">info</span>
              <p>Les options de formatage contrôlent l'affichage final de la valeur.</p>
            </div>

            <div class="options-grid">
              <div
                  v-for="fmt in formattingTypes"
                  :key="fmt.type"
                  class="option-card"
                  :class="{ selected: selectedFormatting === fmt.type }"
                  @click="selectFormatting(fmt)"
              >
                <span class="material-symbols-outlined option-icon">{{ fmt.icon }}</span>
                <div class="option-info">
                  <h5>{{ fmt.label }}</h5>
                  <p>{{ fmt.description }}</p>
                </div>
              </div>
            </div>

            <!-- Formatting Builder -->
            <transition name="slide-fade">
              <div class="builder-panel" v-if="selectedFormatting">
                <div class="builder-grid">
                  <!-- Prefix/Suffix -->
                  <div class="builder-field" v-if="selectedFormatting === 'prefix'">
                    <label>Préfixe</label>
                    <input type="text" v-model="formattingBuilder.prefix" class="builder-input" placeholder="€ " />
                  </div>
                  <div class="builder-field" v-if="selectedFormatting === 'suffix'">
                    <label>Suffixe</label>
                    <input type="text" v-model="formattingBuilder.suffix" class="builder-input" placeholder=" kg" />
                  </div>

                  <!-- Decimals -->
                  <div class="builder-field" v-if="selectedFormatting === 'decimals'">
                    <label>Nombre de décimales</label>
                    <input type="number" v-model="formattingBuilder.decimals" class="builder-input" placeholder="2" min="0" max="10" />
                  </div>

                  <!-- Thousand separator -->
                  <div class="builder-field" v-if="selectedFormatting === 'thousands'">
                    <label>Séparateur</label>
                    <div class="button-group">
                      <button type="button" class="group-btn" :class="{ active: formattingBuilder.separator === ' ' }" @click="formattingBuilder.separator = ' '">Espace</button>
                      <button type="button" class="group-btn" :class="{ active: formattingBuilder.separator === ',' }" @click="formattingBuilder.separator = ','">Virgule</button>
                      <button type="button" class="group-btn" :class="{ active: formattingBuilder.separator === '.' }" @click="formattingBuilder.separator = '.'">Point</button>
                    </div>
                  </div>

                  <!-- Color thresholds -->
                  <template v-if="selectedFormatting === 'colorize'">
                    <div class="builder-field">
                      <label>Seuil vert (bon)</label>
                      <input type="number" v-model="formattingBuilder.greenThreshold" class="builder-input" placeholder="80" step="any" />
                    </div>
                    <div class="builder-field">
                      <label>Seuil rouge (mauvais)</label>
                      <input type="number" v-model="formattingBuilder.redThreshold" class="builder-input" placeholder="20" step="any" />
                    </div>
                    <div class="builder-field">
                      <label>Inversion</label>
                      <label class="toggle-label compact">
                        <input type="checkbox" v-model="formattingBuilder.invertColors" class="toggle-input" />
                        <span class="toggle-switch small"></span>
                        <span class="toggle-text">Valeurs basses = bon</span>
                      </label>
                    </div>
                  </template>
                </div>

                <button type="button" class="btn btn-add" @click="addFormatting" :disabled="!canAddFormatting">
                  <span class="material-symbols-outlined">add</span>
                  Ajouter le formatage
                </button>
              </div>
            </transition>
          </div>

          <!-- ALERTS -->
          <div class="panel-section" v-if="activeCategory === 'alerts'">
            <div class="section-intro">
              <span class="material-symbols-outlined">info</span>
              <p>Les alertes déclenchent des notifications lorsque certaines conditions sont remplies.</p>
            </div>

            <div class="options-grid">
              <div
                  v-for="alert in alertTypes"
                  :key="alert.type"
                  class="option-card"
                  :class="[alert.colorClass, { selected: selectedAlert === alert.type }]"
                  @click="selectAlert(alert)"
              >
                <span class="material-symbols-outlined option-icon">{{ alert.icon }}</span>
                <div class="option-info">
                  <h5>{{ alert.label }}</h5>
                  <p>{{ alert.description }}</p>
                </div>
              </div>
            </div>

            <!-- Alert Builder -->
            <transition name="slide-fade">
              <div class="builder-panel" v-if="selectedAlert">
                <div class="builder-grid">
                  <!-- Threshold -->
                  <div class="builder-field" v-if="alertNeedsThreshold">
                    <label>Seuil</label>
                    <input type="number" v-model="alertBuilder.threshold" class="builder-input" placeholder="0" step="any" />
                  </div>

                  <!-- Comparison for threshold alerts -->
                  <div class="builder-field" v-if="alertNeedsComparison">
                    <label>Condition</label>
                    <div class="button-group">
                      <button type="button" class="group-btn mono" :class="{ active: alertBuilder.operator === '>' }" @click="alertBuilder.operator = '>'"></button>
                      <button type="button" class="group-btn mono" :class="{ active: alertBuilder.operator === '>=' }" @click="alertBuilder.operator = '>='">≥</button>
                      <button type="button" class="group-btn mono" :class="{ active: alertBuilder.operator === '<' }" @click="alertBuilder.operator = '<'"></button>
                      <button type="button" class="group-btn mono" :class="{ active: alertBuilder.operator === '<=' }" @click="alertBuilder.operator = '<='">≤</button>
                    </div>
                  </div>

                  <!-- Duration for sustained alerts -->
                  <div class="builder-field" v-if="selectedAlert === 'sustained'">
                    <label>Durée (minutes)</label>
                    <input type="number" v-model="alertBuilder.duration" class="builder-input" placeholder="5" min="1" />
                  </div>

                  <!-- Rate for rate-of-change -->
                  <div class="builder-field" v-if="selectedAlert === 'rateOfChange'">
                    <label>Variation par minute</label>
                    <input type="number" v-model="alertBuilder.rate" class="builder-input" placeholder="1" step="any" />
                  </div>

                  <!-- Severity -->
                  <div class="builder-field">
                    <label>Sévérité</label>
                    <div class="button-group">
                      <button type="button" class="group-btn severity-info" :class="{ active: alertBuilder.severity === 'info' }" @click="alertBuilder.severity = 'info'">Info</button>
                      <button type="button" class="group-btn severity-warning" :class="{ active: alertBuilder.severity === 'warning' }" @click="alertBuilder.severity = 'warning'">Avertissement</button>
                      <button type="button" class="group-btn severity-critical" :class="{ active: alertBuilder.severity === 'critical' }" @click="alertBuilder.severity = 'critical'">Critique</button>
                    </div>
                  </div>

                  <!-- Message -->
                  <div class="builder-field full-width">
                    <label>Message personnalisé (optionnel)</label>
                    <input type="text" v-model="alertBuilder.message" class="builder-input" placeholder="Alerte: valeur anormale détectée" />
                  </div>
                </div>

                <button type="button" class="btn btn-add" @click="addAlert" :disabled="!canAddAlert">
                  <span class="material-symbols-outlined">add</span>
                  Ajouter l'alerte
                </button>
              </div>
            </transition>
          </div>
        </div>

        <!-- JSON Output -->
        <div class="output-section" v-if="rules.length > 0 && 1 > 0">
          <div class="output-header">
            <div class="output-label">
              <span class="material-symbols-outlined">data_object</span>
              Sortie JSON
            </div>
            <button type="button" class="btn-icon" @click="copyJson" title="Copier">
              <span class="material-symbols-outlined">{{ copied ? 'check' : 'content_copy' }}</span>
            </button>
          </div>
          <pre class="output-code">{{ jsonOutputFormatted }}</pre>
        </div>

        <!-- Actions -->
        <div class="calculator-actions">
          <button type="button" class="btn btn-ghost" @click="importJson">
            <span class="material-symbols-outlined">upload</span>
            Importer
          </button>
          <div class="actions-spacer"></div>
          <button type="button" class="btn btn-danger-ghost" @click="clearAll" :disabled="rules.length === 0">
            <span class="material-symbols-outlined">delete</span>
            Tout effacer
          </button>
        </div>
      </div>
    </transition>

    <!-- Import Modal -->
    <transition name="modal-fade">
      <div class="modal-overlay" v-if="showImportModal" @click.self="showImportModal = false">
        <div class="modal-card">
          <div class="modal-header">
            <h3>Importer JSON</h3>
            <button type="button" class="modal-close" @click="showImportModal = false">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <div class="modal-body">
            <textarea
                v-model="importText"
                class="import-textarea"
                placeholder='{"rules": [...]}'
                rows="8"
            ></textarea>
            <p class="import-error" v-if="importError">{{ importError }}</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-ghost" @click="showImportModal = false">Annuler</button>
            <button type="button" class="btn btn-primary" @click="doImport">Importer</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { isValidTemporalValue } from '@/extlibs/formulaDates';

export default {
  name: 'SensorFormulaCalculator',

  props: {
    value: {
      type: [String, Object],
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      expanded: false,
      activeCategory: 'conditions',
      rules: [],
      ruleIdCounter: 0,
      copied: false,
      showImportModal: false,
      importText: '',
      importError: '',

      // Categories
      categories: [
        { id: 'conditions', label: 'Conditions', icon: 'filter_alt' },
        { id: 'operations', label: 'Opérations', icon: 'calculate' },
        { id: 'formatting', label: 'Formatage', icon: 'format_paint' },
        // { id: 'alerts', label: 'Alertes', icon: 'notifications' }
      ],

      // Selected items
      selectedCondition: null,
      selectedOperation: null,
      selectedFormatting: null,
      selectedAlert: null,

      // Builders
      conditionBuilder: this.getEmptyConditionBuilder(),
      operationBuilder: this.getEmptyOperationBuilder(),
      formattingBuilder: this.getEmptyFormattingBuilder(),
      alertBuilder: this.getEmptyAlertBuilder(),

      // Condition types
      conditionTypes: [
        { type: 'valueCompare', label: 'Comparaison de valeur', description: 'Compare la valeur brute ou finale', icon: 'compare' },
        { type: 'dateCompare', label: 'Date', description: 'Compare le jour de la mesure à une date', icon: 'event' },
        { type: 'dateTimeCompare', label: 'DateTime', description: 'Compare la date et l’heure de la mesure', icon: 'event_available' },
        { type: 'timeCompare', label: 'Time', description: 'Compare l’heure de la mesure', icon: 'schedule' },
        { type: 'range', label: 'Plage de valeurs', description: 'Vérifie si la valeur est dans une plage', icon: 'linear_scale' },
        { type: 'isNull', label: 'Valeur nulle', description: 'Vérifie si la valeur est nulle ou manquante', icon: 'block' },
        { type: 'isValid', label: 'Valeur valide', description: 'Vérifie si la valeur est un nombre valide', icon: 'check_circle' },
        { type: 'timeRange', label: 'Plage horaire', description: 'Active uniquement pendant certaines heures', icon: 'schedule' },
        { type: 'dayOfWeek', label: 'Jour de la semaine', description: 'Active uniquement certains jours', icon: 'calendar_today' },
        { type: 'deltaCompare', label: 'Variation', description: 'Compare la variation depuis la dernière valeur', icon: 'trending_up' },
        { type: 'percentChange', label: 'Variation en %', description: 'Compare le changement en pourcentage', icon: 'percent' },
        { type: 'consecutiveCount', label: 'Valeurs consécutives', description: 'Compte les valeurs consécutives répondant à un critère', icon: 'format_list_numbered' },
        { type: 'custom', label: 'Expression personnalisée', description: 'Écrivez votre propre condition JS', icon: 'code' }
      ],

      // OPERATION TYPES (15 types)
      operationTypes: [
        { type: 'add', label: 'Ajouter', description: 'Additionne une constante', icon: 'add', colorClass: 'op-add' },
        { type: 'subtract', label: 'Soustraire', description: 'Soustrait une constante', icon: 'remove', colorClass: 'op-subtract' },
        { type: 'multiply', label: 'Multiplier', description: 'Multiplie par une constante', icon: 'close', colorClass: 'op-multiply' },
        { type: 'divide', label: 'Diviser', description: 'Divise par une constante', icon: 'percent', colorClass: 'op-divide' },
        { type: 'power', label: 'Puissance', description: 'Élève à une puissance', icon: 'superscript', colorClass: 'op-power' },
        { type: 'sqrt', label: 'Racine carrée', description: 'Calcule la racine carrée', icon: 'square_root', colorClass: 'op-sqrt' },
        { type: 'abs', label: 'Valeur absolue', description: 'Retourne la valeur absolue', icon: 'exposure', colorClass: 'op-abs' },
        { type: 'round', label: 'Arrondir', description: 'Arrondit à N décimales', icon: 'decimal_increase', colorClass: 'op-round' },
        { type: 'floor', label: 'Arrondir inf.', description: 'Arrondit vers le bas', icon: 'arrow_downward', colorClass: 'op-floor' },
        { type: 'ceil', label: 'Arrondir sup.', description: 'Arrondit vers le haut', icon: 'arrow_upward', colorClass: 'op-ceil' },
        { type: 'clamp', label: 'Limiter', description: 'Limite entre min et max', icon: 'compress', colorClass: 'op-clamp' },
        { type: 'map', label: 'Mapper', description: 'Convertit d\'une plage à une autre', icon: 'swap_horiz', colorClass: 'op-map' },
        { type: 'log', label: 'Logarithme', description: 'Calcule le logarithme', icon: 'functions', colorClass: 'op-log' },
        { type: 'movingAvg', label: 'Moyenne mobile', description: 'Lisse les données sur N points', icon: 'show_chart', colorClass: 'op-avg' },
        { type: 'deadband', label: 'Zone morte', description: 'Ignore les petites variations', icon: 'unfold_less', colorClass: 'op-deadband' },
        { type: 'previousValue', label: 'Valeur précédente', description: 'Utilise la valeur du point précédent', icon: 'history', colorClass: 'op-prev' },
        { type: 'delta', label: 'Delta', description: 'Calcule la différence avec la valeur précédente', icon: 'difference', colorClass: 'op-delta' },
        { type: 'cumulative', label: 'Cumulatif', description: 'Somme cumulative des valeurs', icon: 'stacked_line_chart', colorClass: 'op-cumul' },
        { type: 'negate', label: 'Inverser signe', description: 'Inverse le signe de la valeur', icon: 'flip', colorClass: 'op-negate' },
        { type: 'modulo', label: 'Modulo', description: 'Reste de la division', icon: 'more_horiz', colorClass: 'op-modulo' }
      ],

      // FORMATTING TYPES (8 types)
      formattingTypes: [
        { type: 'prefix', label: 'Préfixe', description: 'Ajoute du texte avant la valeur', icon: 'first_page' },
        { type: 'suffix', label: 'Suffixe', description: 'Ajoute du texte après la valeur', icon: 'last_page' },
        { type: 'decimals', label: 'Décimales', description: 'Fixe le nombre de décimales', icon: 'decimal_increase' },
        { type: 'thousands', label: 'Séparateur milliers', description: 'Formate les grands nombres', icon: 'format_list_numbered' },
        { type: 'percentage', label: 'Pourcentage', description: 'Affiche en pourcentage', icon: 'percent' },
        { type: 'scientific', label: 'Notation scientifique', description: 'Affiche en notation scientifique', icon: 'science' },
        { type: 'colorize', label: 'Colorisation', description: 'Colore selon la valeur', icon: 'palette' },
        { type: 'hideZero', label: 'Masquer zéro', description: 'N\'affiche rien si la valeur est 0', icon: 'visibility_off' }
      ],

      // ALERT TYPES (7 types)
      alertTypes: [
        { type: 'threshold', label: 'Seuil simple', description: 'Alerte quand la valeur dépasse un seuil', icon: 'warning', colorClass: 'alert-warning' },
        { type: 'outOfRange', label: 'Hors plage', description: 'Alerte quand la valeur sort d\'une plage', icon: 'error_outline', colorClass: 'alert-danger' },
        { type: 'sustained', label: 'Seuil maintenu', description: 'Alerte si le seuil est dépassé pendant X minutes', icon: 'timer', colorClass: 'alert-warning' },
        { type: 'rateOfChange', label: 'Variation rapide', description: 'Alerte si la valeur change trop vite', icon: 'speed', colorClass: 'alert-danger' },
        { type: 'noData', label: 'Absence de données', description: 'Alerte si aucune donnée reçue', icon: 'signal_wifi_off', colorClass: 'alert-info' },
        { type: 'anomaly', label: 'Anomalie', description: 'Détecte les valeurs anormales (écart-type)', icon: 'psychology', colorClass: 'alert-warning' },
        { type: 'recovery', label: 'Retour à la normale', description: 'Notifie quand la valeur revient dans les limites', icon: 'check_circle', colorClass: 'alert-success' }
      ],

      // Value sources
      valueSources: [
        { value: 'raw', label: 'Valeur brute' },
        { value: 'final', label: 'Valeur finale' }
      ],

      // Comparison operators
      comparisonOperators: [
        { value: '>', display: '>' },
        { value: '>=', display: '≥' },
        { value: '<', display: '<' },
        { value: '<=', display: '≤' },
        { value: '==', display: '=' },
        { value: '!=', display: '≠' }
      ],

      // Week days
      weekDays: [
        { value: 1, short: 'L', full: 'Lundi' },
        { value: 2, short: 'M', full: 'Mardi' },
        { value: 3, short: 'M', full: 'Mercredi' },
        { value: 4, short: 'J', full: 'Jeudi' },
        { value: 5, short: 'V', full: 'Vendredi' },
        { value: 6, short: 'S', full: 'Samedi' },
        { value: 0, short: 'D', full: 'Dimanche' }
      ]
    };
  },

  computed: {
    rulesPreviewText() {
      const counts = {};
      this.rules.forEach(r => {
        counts[r.category] = (counts[r.category] || 0) + 1;
      });
      const parts = [];
      if (counts.conditions) parts.push(`${counts.conditions} cond.`);
      if (counts.operations) parts.push(`${counts.operations} op.`);
      if (counts.formatting) parts.push(`${counts.formatting} fmt.`);
      if (counts.alerts) parts.push(`${counts.alerts} alerte${counts.alerts > 1 ? 's' : ''}`);
      return parts.join(', ');
    },

    allEnabled() {
      return this.rules.every(r => r.enabled);
    },

    jsonOutput() {
      return {
        version: 2,
        rules: this.rules.map(r => {
          const rule = { ...r };
          delete rule.id;
          return rule;
        })
      };
    },

    jsonOutputFormatted() {
      return JSON.stringify(this.jsonOutput, null, 2);
    },

    // Condition builder computeds
    needsSource() {
      return ['valueCompare', 'range', 'deltaCompare', 'percentChange'].includes(this.selectedCondition);
    },
    needsComparison() {
      return this.needsTemporalValue || ['valueCompare', 'deltaCompare', 'percentChange'].includes(this.selectedCondition);
    },
    needsTemporalValue() {
      return ['dateCompare', 'dateTimeCompare', 'timeCompare'].includes(this.selectedCondition);
    },
    conditionComparisonOperators() {
      return this.needsTemporalValue
          ? [{ value: '<', display: '<' }, { value: '==', display: '=' }, { value: '>', display: '>' }]
          : this.comparisonOperators;
    },
    temporalInputType() {
      return { dateCompare: 'date', dateTimeCompare: 'datetime-local', timeCompare: 'time' }[this.selectedCondition];
    },
    temporalValueLabel() {
      return { dateCompare: 'Date', dateTimeCompare: 'Date et heure', timeCompare: 'Heure' }[this.selectedCondition];
    },
    needsValue() {
      return ['valueCompare', 'deltaCompare', 'percentChange', 'consecutiveCount'].includes(this.selectedCondition);
    },
    needsRange() {
      return this.selectedCondition === 'range';
    },
    needsTime() {
      return this.selectedCondition === 'timeRange';
    },
    needsDays() {
      return this.selectedCondition === 'dayOfWeek';
    },
    canBeShowOnly() {
      return this.needsTemporalValue || ['valueCompare', 'range', 'timeRange', 'dayOfWeek', 'isValid'].includes(this.selectedCondition);
    },
    canAddCondition() {
      if (!this.selectedCondition) return false;
      switch (this.selectedCondition) {
        case 'dateCompare':
        case 'dateTimeCompare':
        case 'timeCompare':
          return isValidTemporalValue(this.selectedCondition, this.conditionBuilder.value)
              && ['<', '==', '>'].includes(this.conditionBuilder.operator);
        case 'valueCompare':
        case 'deltaCompare':
        case 'percentChange':
          return this.conditionBuilder.value !== '' && this.conditionBuilder.value !== null;
        case 'range':
          return this.conditionBuilder.min !== '' && this.conditionBuilder.max !== '';
        case 'timeRange':
          return this.conditionBuilder.startTime && this.conditionBuilder.endTime;
        case 'dayOfWeek':
          return this.conditionBuilder.days && this.conditionBuilder.days.length > 0;
        case 'consecutiveCount':
          return this.conditionBuilder.value !== '' && this.conditionBuilder.value !== null;
        default:
          return true;
      }
    },

    // Operation builder computeds
    operationNeedsValue() {
      return ['add', 'subtract', 'multiply', 'divide', 'power', 'log', 'modulo'].includes(this.selectedOperation);
    },
    canAddOperation() {
      if (!this.selectedOperation) return false;
      switch (this.selectedOperation) {
        case 'add':
        case 'subtract':
        case 'multiply':
        case 'divide':
        case 'power':
        case 'modulo':
          return this.operationBuilder.value !== '' && this.operationBuilder.value !== null;
        case 'round':
          return true; // precision has default
        case 'clamp':
          return this.operationBuilder.min !== '' && this.operationBuilder.max !== '';
        case 'map':
          return this.operationBuilder.inMin !== '' && this.operationBuilder.inMax !== '' &&
              this.operationBuilder.outMin !== '' && this.operationBuilder.outMax !== '';
        case 'movingAvg':
          return this.operationBuilder.window > 1;
        case 'deadband':
          return this.operationBuilder.threshold !== '' && this.operationBuilder.threshold !== null;
        default:
          return true;
      }
    },

    // Formatting computeds
    canAddFormatting() {
      if (!this.selectedFormatting) return false;
      switch (this.selectedFormatting) {
        case 'prefix':
          return this.formattingBuilder.prefix !== '';
        case 'suffix':
          return this.formattingBuilder.suffix !== '';
        case 'decimals':
          return this.formattingBuilder.decimals !== '' && this.formattingBuilder.decimals >= 0;
        case 'colorize':
          return this.formattingBuilder.greenThreshold !== '' && this.formattingBuilder.redThreshold !== '';
        default:
          return true;
      }
    },

    // Alert computeds
    alertNeedsThreshold() {
      return ['threshold', 'sustained', 'anomaly'].includes(this.selectedAlert);
    },
    alertNeedsComparison() {
      return ['threshold', 'sustained'].includes(this.selectedAlert);
    },
    canAddAlert() {
      if (!this.selectedAlert) return false;
      switch (this.selectedAlert) {
        case 'threshold':
        case 'sustained':
          return this.alertBuilder.threshold !== '' && this.alertBuilder.threshold !== null;
        case 'rateOfChange':
          return this.alertBuilder.rate !== '' && this.alertBuilder.rate !== null;
        default:
          return true;
      }
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.parseValue(newVal);
      }
    },

    jsonOutput: {
      deep: true,
      handler(newVal) {
        this.$emit('input', JSON.stringify(newVal));
      }
    }
  },

  methods: {
    // Empty builders
    getEmptyConditionBuilder() {
      return {
        source: 'raw',
        operator: '>',
        value: '',
        min: '',
        max: '',
        startTime: '',
        endTime: '',
        days: [],
        showOnly: false
      };
    },

    getEmptyOperationBuilder() {
      return {
        value: '',
        precision: 2,
        min: '',
        max: '',
        inMin: '',
        inMax: '',
        outMin: '',
        outMax: '',
        window: 5,
        threshold: ''
      };
    },

    getEmptyFormattingBuilder() {
      return {
        prefix: '',
        suffix: '',
        decimals: 2,
        separator: ' ',
        greenThreshold: '',
        redThreshold: '',
        invertColors: false
      };
    },

    getEmptyAlertBuilder() {
      return {
        threshold: '',
        operator: '>',
        duration: 5,
        rate: '',
        severity: 'warning',
        message: ''
      };
    },

    toggleExpanded() {
      if (!this.disabled) {
        this.expanded = !this.expanded;
      }
    },

    generateId() {
      return ++this.ruleIdCounter;
    },

    parseValue(val) {
      if (!val) {
        this.rules = [];
        return;
      }

      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val;
        if (parsed && parsed.rules) {
          this.rules = parsed.rules.map(r => ({
            ...r,
            id: this.generateId()
          }));
        }
      } catch (e) {
        // Try to parse legacy format
        this.parseLegacyFormat(val);
      }
    },

    parseLegacyFormat(val) {
      // Parse old format: operator!value!operator!value
      if (typeof val !== 'string') return;

      const parts = val.split('!');
      this.rules = [];

      for (let i = 0; i < parts.length - 1; i += 2) {
        const op = parts[i];
        const value = parts[i + 1];

        // Detect condition vs operation
        if (['>', '>=', '<', '<='].some(c => op.includes(c))) {
          let source = 'raw';
          let operator = op;
          let showOnly = false;

          if (op.startsWith('sh')) {
            showOnly = true;
            operator = op.substring(2);
          } else if (op.startsWith('f')) {
            source = 'final';
            operator = op.substring(1);
          }

          this.rules.push({
            id: this.generateId(),
            category: 'conditions',
            type: 'valueCompare',
            enabled: true,
            source,
            operator,
            value: parseFloat(value),
            showOnly
          });
        } else if (['+', '-', '*', '/'].includes(op)) {
          const typeMap = { '+': 'add', '-': 'subtract', '*': 'multiply', '/': 'divide' };
          this.rules.push({
            id: this.generateId(),
            category: 'operations',
            type: typeMap[op],
            enabled: true,
            value: parseFloat(value)
          });
        }
      }
    },

    getCategoryCount(catId) {
      return this.rules.filter(r => r.category === catId).length;
    },

    toggleAllRules() {
      const newState = !this.allEnabled;
      this.rules.forEach(r => r.enabled = newState);
      this.emitUpdate();
    },

    removeRule(index) {
      this.rules.splice(index, 1);
      this.emitUpdate();
    },

    clearAll() {
      this.rules = [];
      this.emitUpdate();
    },

    emitUpdate() {
      this.$emit('input', JSON.stringify(this.jsonOutput));
    },

    // Selection methods
    selectCondition(cond) {
      this.selectedCondition = this.selectedCondition === cond.type ? null : cond.type;
      this.conditionBuilder = this.getEmptyConditionBuilder();
    },

    selectOperation(op) {
      this.selectedOperation = this.selectedOperation === op.type ? null : op.type;
      this.operationBuilder = this.getEmptyOperationBuilder();
    },

    selectFormatting(fmt) {
      this.selectedFormatting = this.selectedFormatting === fmt.type ? null : fmt.type;
      this.formattingBuilder = this.getEmptyFormattingBuilder();
    },

    selectAlert(alert) {
      this.selectedAlert = this.selectedAlert === alert.type ? null : alert.type;
      this.alertBuilder = this.getEmptyAlertBuilder();
    },

    // Toggle day
    toggleDay(day) {
      if (!this.conditionBuilder.days) this.conditionBuilder.days = [];
      const idx = this.conditionBuilder.days.indexOf(day);
      if (idx >= 0) {
        this.conditionBuilder.days.splice(idx, 1);
      } else {
        this.conditionBuilder.days.push(day);
      }
    },

    // Add methods
    addCondition() {
      if (!this.canAddCondition) return;

      const rule = {
        id: this.generateId(),
        category: 'conditions',
        type: this.selectedCondition,
        enabled: true
      };

      // Add specific fields based on type
      switch (this.selectedCondition) {
        case 'dateCompare':
        case 'dateTimeCompare':
        case 'timeCompare':
          rule.operator = this.conditionBuilder.operator;
          rule.value = this.conditionBuilder.value;
          rule.showOnly = this.conditionBuilder.showOnly;
          break;
        case 'valueCompare':
        case 'deltaCompare':
        case 'percentChange':
          rule.source = this.conditionBuilder.source;
          rule.operator = this.conditionBuilder.operator;
          rule.value = parseFloat(this.conditionBuilder.value);
          rule.showOnly = this.conditionBuilder.showOnly;
          break;
        case 'range':
          rule.source = this.conditionBuilder.source;
          rule.min = parseFloat(this.conditionBuilder.min);
          rule.max = parseFloat(this.conditionBuilder.max);
          rule.showOnly = this.conditionBuilder.showOnly;
          break;
        case 'timeRange':
          rule.startTime = this.conditionBuilder.startTime;
          rule.endTime = this.conditionBuilder.endTime;
          rule.showOnly = this.conditionBuilder.showOnly;
          break;
        case 'dayOfWeek':
          rule.days = [...this.conditionBuilder.days];
          rule.showOnly = this.conditionBuilder.showOnly;
          break;
        case 'consecutiveCount':
          rule.operator = this.conditionBuilder.operator;
          rule.value = parseInt(this.conditionBuilder.value);
          break;
        case 'isNull':
        case 'isValid':
          rule.showOnly = this.conditionBuilder.showOnly;
          break;
      }

      this.rules.push(rule);
      this.selectedCondition = null;
      this.conditionBuilder = this.getEmptyConditionBuilder();
      this.emitUpdate();
    },

    addOperation() {
      if (!this.canAddOperation) return;

      const rule = {
        id: this.generateId(),
        category: 'operations',
        type: this.selectedOperation,
        enabled: true
      };

      switch (this.selectedOperation) {
        case 'add':
        case 'subtract':
        case 'multiply':
        case 'divide':
        case 'power':
        case 'modulo':
          rule.value = parseFloat(this.operationBuilder.value);
          break;
        case 'round':
          rule.precision = parseInt(this.operationBuilder.precision) || 2;
          break;
        case 'clamp':
          rule.min = parseFloat(this.operationBuilder.min);
          rule.max = parseFloat(this.operationBuilder.max);
          break;
        case 'map':
          rule.inMin = parseFloat(this.operationBuilder.inMin);
          rule.inMax = parseFloat(this.operationBuilder.inMax);
          rule.outMin = parseFloat(this.operationBuilder.outMin);
          rule.outMax = parseFloat(this.operationBuilder.outMax);
          break;
        case 'movingAvg':
          rule.window = parseInt(this.operationBuilder.window);
          break;
        case 'deadband':
          rule.threshold = parseFloat(this.operationBuilder.threshold);
          break;
        case 'log':
          rule.base = this.operationBuilder.value ? parseFloat(this.operationBuilder.value) : 10;
          break;
      }

      this.rules.push(rule);
      this.selectedOperation = null;
      this.operationBuilder = this.getEmptyOperationBuilder();
      this.emitUpdate();
    },

    addFormatting() {
      if (!this.canAddFormatting) return;

      const rule = {
        id: this.generateId(),
        category: 'formatting',
        type: this.selectedFormatting,
        enabled: true
      };

      switch (this.selectedFormatting) {
        case 'prefix':
          rule.prefix = this.formattingBuilder.prefix;
          break;
        case 'suffix':
          rule.suffix = this.formattingBuilder.suffix;
          break;
        case 'decimals':
          rule.decimals = parseInt(this.formattingBuilder.decimals);
          break;
        case 'thousands':
          rule.separator = this.formattingBuilder.separator;
          break;
        case 'colorize':
          rule.greenThreshold = parseFloat(this.formattingBuilder.greenThreshold);
          rule.redThreshold = parseFloat(this.formattingBuilder.redThreshold);
          rule.invertColors = this.formattingBuilder.invertColors;
          break;
      }

      this.rules.push(rule);
      this.selectedFormatting = null;
      this.formattingBuilder = this.getEmptyFormattingBuilder();
      this.emitUpdate();
    },

    addAlert() {
      if (!this.canAddAlert) return;

      const rule = {
        id: this.generateId(),
        category: 'alerts',
        type: this.selectedAlert,
        enabled: true,
        severity: this.alertBuilder.severity
      };

      if (this.alertBuilder.message) {
        rule.message = this.alertBuilder.message;
      }

      switch (this.selectedAlert) {
        case 'threshold':
          rule.operator = this.alertBuilder.operator;
          rule.threshold = parseFloat(this.alertBuilder.threshold);
          break;
        case 'sustained':
          rule.operator = this.alertBuilder.operator;
          rule.threshold = parseFloat(this.alertBuilder.threshold);
          rule.duration = parseInt(this.alertBuilder.duration);
          break;
        case 'rateOfChange':
          rule.rate = parseFloat(this.alertBuilder.rate);
          break;
        case 'outOfRange':
          rule.min = parseFloat(this.alertBuilder.min || 0);
          rule.max = parseFloat(this.alertBuilder.max || 100);
          break;
        case 'anomaly':
          rule.threshold = parseFloat(this.alertBuilder.threshold) || 2; // std deviations
          break;
      }

      this.rules.push(rule);
      this.selectedAlert = null;
      this.alertBuilder = this.getEmptyAlertBuilder();
      this.emitUpdate();
    },

    // Helper methods for display
    getRuleIcon(rule) {
      const allTypes = [...this.conditionTypes, ...this.operationTypes, ...this.formattingTypes, ...this.alertTypes];
      const found = allTypes.find(t => t.type === rule.type);
      return found ? found.icon : 'help';
    },

    getRuleLabel(rule) {
      const allTypes = [...this.conditionTypes, ...this.operationTypes, ...this.formattingTypes, ...this.alertTypes];
      const found = allTypes.find(t => t.type === rule.type);
      return found ? found.label : rule.type;
    },

    getRuleDescription(rule) {
      switch (rule.type) {
        case 'dateCompare':
        case 'dateTimeCompare':
        case 'timeCompare':
          return `${rule.operator === '==' ? '=' : rule.operator} ${String(rule.value || '').replace('T', ' ')}${rule.showOnly ? ' (affichage)' : ''}`;
        case 'valueCompare':
          return `${rule.source === 'final' ? 'finale' : 'brute'} ${rule.operator} ${rule.value}${rule.showOnly ? ' (affichage)' : ''}`;
        case 'range':
          return `${rule.min} ≤ valeur ≤ ${rule.max}`;
        case 'timeRange':
          return `${rule.startTime} - ${rule.endTime}`;
        case 'dayOfWeek':
          return rule.days.map(d => this.weekDays.find(wd => wd.value === d)?.short).join(', ');
        case 'add':
          return `+ ${rule.value}`;
        case 'subtract':
          return `− ${rule.value}`;
        case 'multiply':
          return `× ${rule.value}`;
        case 'divide':
          return `÷ ${rule.value}`;
        case 'round':
          return `${rule.precision} décimales`;
        case 'clamp':
          return `[${rule.min}, ${rule.max}]`;
        case 'map':
          return `[${rule.inMin}-${rule.inMax}] → [${rule.outMin}-${rule.outMax}]`;
        case 'prefix':
          return `"${rule.prefix}"`;
        case 'suffix':
          return `"${rule.suffix}"`;
        case 'decimals':
          return `${rule.decimals} décimales`;
        case 'threshold':
          return `${rule.operator} ${rule.threshold} (${rule.severity})`;
        case 'sustained':
          return `${rule.operator} ${rule.threshold} pendant ${rule.duration}min`;
        default:
          return '';
      }
    },

    getOperationValueLabel() {
      switch (this.selectedOperation) {
        case 'power': return 'Exposant';
        case 'log': return 'Base (défaut: 10)';
        case 'modulo': return 'Diviseur';
        default: return 'Valeur';
      }
    },

    getOperationPlaceholder() {
      switch (this.selectedOperation) {
        case 'power': return '2';
        case 'log': return '10';
        default: return '0';
      }
    },

    // Copy JSON
    async copyJson() {
      try {
        await navigator.clipboard.writeText(this.jsonOutputFormatted);
        this.copied = true;
        setTimeout(() => this.copied = false, 2000);
      } catch (e) {
        console.error('Copy failed', e);
      }
    },

    // Import
    importJson() {
      this.importText = '';
      this.importError = '';
      this.showImportModal = true;
    },

    doImport() {
      try {
        const parsed = JSON.parse(this.importText);
        if (parsed && parsed.rules) {
          this.rules = parsed.rules.map(r => ({
            ...r,
            id: this.generateId()
          }));
          this.showImportModal = false;
          this.emitUpdate();
        } else {
          this.importError = 'Format invalide: objet "rules" manquant';
        }
      } catch (e) {
        this.importError = 'JSON invalide: ' + e.message;
      }
    }
  }
};
</script>

<style scoped>
/* ============================================
   CSS VARIABLES
   ============================================ */
.formula-calculator {
  --color-primary: #2d5a3d;
  --color-primary-light: #3d7a52;
  --color-primary-lighter: #5a9e6f;
  --color-primary-dark: #1e3d29;
  --color-primary-bg: #e8f0eb;

  --color-accent: #5a9e6f;
  --color-accent-light: #a8d4b8;

  --color-surface: #ffffff;
  --color-surface-secondary: #fafbfa;
  --color-surface-hover: #f0f4f1;
  --color-background: #f5f7f6;

  --color-border: #e2e8e4;
  --color-border-light: #f0f4f1;

  --color-text-primary: #1a2e1f;
  --color-text-secondary: #5a6b5e;
  --color-text-muted: #8a998e;

  --color-danger: #dc2626;
  --color-danger-bg: #fef2f2;
  --color-warning: #d97706;
  --color-warning-bg: #fffbeb;
  --color-success: #059669;
  --color-success-bg: #ecfdf5;
  --color-info: #2563eb;
  --color-info-bg: #eff6ff;

  --color-condition: #7c3aed;
  --color-condition-bg: #f5f3ff;
  --color-operation: #2563eb;
  --color-operation-bg: #eff6ff;
  --color-formatting: #d97706;
  --color-formatting-bg: #fffbeb;
  --color-alerts: #dc2626;
  --color-alerts-bg: #fef2f2;

  --shadow-sm: 0 1px 2px rgba(45, 90, 61, 0.05);
  --shadow-md: 0 4px 12px rgba(45, 90, 61, 0.08);
  --shadow-lg: 0 12px 40px rgba(45, 90, 61, 0.12);

  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 16px;

  --transition-fast: 150ms ease;
  --transition-normal: 200ms cubic-bezier(0.4, 0, 0.2, 1);

  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

/* ============================================
   HEADER
   ============================================ */
.calculator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--color-surface-secondary);
  cursor: pointer;
  transition: var(--transition-normal);
  user-select: none;
}

.calculator-header:hover {
  background: var(--color-surface-hover);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-icon {
  font-size: 22px;
  color: var(--color-accent);
}

.header-info {
  flex: 1;
  min-width: 0;
}

.header-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.header-preview {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  margin: 4px 0 0;
}

.header-preview.empty {
  color: var(--color-text-muted);
  font-style: italic;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.formula-badge {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-primary);
  background: var(--color-primary-bg);
  padding: 4px 10px;
  border-radius: 20px;
}

.expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-normal);
}

.expand-btn:hover {
  background: var(--color-border-light);
}

.expand-btn .material-symbols-outlined {
  font-size: 20px;
  transition: transform var(--transition-normal);
}

.expand-btn.expanded .material-symbols-outlined {
  transform: rotate(180deg);
}

/* ============================================
   BODY
   ============================================ */
.calculator-body {
  padding: 20px;
  border-top: 1px solid var(--color-border-light);
  display: flex;
  flex-direction: column;
  gap: 20px;
  /*max-height: 70vh;
  overflow-y: auto;*/
  overflow: hidden;
}

/* ============================================
   CATEGORY TABS
   ============================================ */
.category-tabs {
  display: flex;
  gap: 8px;
  padding: 4px;
  background: var(--color-surface-secondary);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.category-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition-normal);
  white-space: nowrap;
  font-family: inherit;
}

.category-tab:hover {
  background: var(--color-surface-hover);
  color: var(--color-text-primary);
}

.category-tab.active {
  background: var(--color-surface);
  color: var(--color-primary);
  box-shadow: var(--shadow-sm);
}

.category-tab .material-symbols-outlined {
  font-size: 18px;
}

.tab-count {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 2px 6px;
  background: var(--color-primary-bg);
  color: var(--color-primary);
  border-radius: 10px;
}

/* ============================================
   RULES LIST
   ============================================ */
.rules-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rules-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rules-header .material-symbols-outlined {
  font-size: 18px;
  color: var(--color-accent);
}

.rules-header h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
  flex: 1;
}

.rules-actions .btn-icon {
  padding: 6px;
}

.empty-rules {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 20px;
  background: var(--color-surface-secondary);
  border: 2px dashed var(--color-border);
  border-radius: var(--radius-md);
  text-align: center;
}

.empty-rules .material-symbols-outlined {
  font-size: 32px;
  color: var(--color-text-muted);
  opacity: 0.5;
}

.empty-rules p {
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin: 0;
}

.empty-rules .hint {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
}

.rules-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Rule Card */
.rule-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  transition: var(--transition-normal);
}

.rule-card:hover {
  box-shadow: var(--shadow-sm);
}

.rule-card.disabled {
  opacity: 0.5;
}

.rule-card.conditions {
  border-left: 3px solid var(--color-condition);
}

.rule-card.operations {
  border-left: 3px solid var(--color-operation);
}

.rule-card.formatting {
  border-left: 3px solid var(--color-formatting);
}

.rule-card.alerts {
  border-left: 3px solid var(--color-alerts);
}

.rule-drag {
  cursor: grab;
  color: var(--color-text-muted);
  padding: 2px;
}

.rule-drag:active {
  cursor: grabbing;
}

.rule-drag .material-symbols-outlined {
  font-size: 16px;
}

.rule-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--color-success);
  cursor: pointer;
  padding: 0;
}

.rule-card.disabled .rule-toggle {
  color: var(--color-text-muted);
}

.rule-toggle .material-symbols-outlined {
  font-size: 20px;
}

.rule-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  flex-wrap: wrap;
  min-width: 0;
}

.rule-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.rule-tag.conditions {
  background: var(--color-condition-bg);
  color: var(--color-condition);
}

.rule-tag.operations {
  background: var(--color-operation-bg);
  color: var(--color-operation);
}

.rule-tag.formatting {
  background: var(--color-formatting-bg);
  color: var(--color-formatting);
}

.rule-tag.alerts {
  background: var(--color-alerts-bg);
  color: var(--color-alerts);
}

.rule-tag .material-symbols-outlined {
  font-size: 12px;
}

.rule-description {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  font-family: 'SF Mono', monospace;
}

.rule-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-fast);
  flex-shrink: 0;
}

.rule-delete:hover {
  background: var(--color-danger-bg);
  color: var(--color-danger);
}

/* ============================================
   ADD PANEL
   ============================================ */
.add-panel {
  background: var(--color-surface-secondary);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.panel-section {
  padding: 20px;
}

.section-intro {
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  background: var(--color-info-bg);
  border-radius: var(--radius-sm);
  margin-bottom: 16px;
  border-left: 3px solid var(--color-info);
}

.section-intro .material-symbols-outlined {
  font-size: 18px;
  color: var(--color-info);
  flex-shrink: 0;
}

.section-intro p {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

/* ============================================
   OPTIONS GRID
   ============================================ */
.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.option-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-normal);
}

.option-card:hover {
  border-color: var(--color-primary-lighter);
  box-shadow: var(--shadow-sm);
}

.option-card.selected {
  border-color: var(--color-primary);
  background: var(--color-primary-bg);
}

.option-icon {
  font-size: 20px;
  color: var(--color-accent);
  flex-shrink: 0;
}

.option-info h5 {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0 0 4px;
}

.option-info p {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin: 0;
  line-height: 1.4;
}

/* Color classes for operation cards */
.option-card.op-add .option-icon { color: #059669; }
.option-card.op-subtract .option-icon { color: #dc2626; }
.option-card.op-multiply .option-icon { color: #7c3aed; }
.option-card.op-divide .option-icon { color: #2563eb; }

.option-card.alert-warning .option-icon { color: #d97706; }
.option-card.alert-danger .option-icon { color: #dc2626; }
.option-card.alert-info .option-icon { color: #2563eb; }
.option-card.alert-success .option-icon { color: #059669; }

/* ============================================
   BUILDER PANEL
   ============================================ */
.builder-panel {
  padding: 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  margin-top: 16px;
}

.builder-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.builder-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.builder-field.full-width {
  grid-column: 1 / -1;
}

.builder-field label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.builder-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.9375rem;
  color: var(--color-text-primary);
  background: var(--color-surface);
  transition: var(--transition-normal);
  font-family: inherit;
}

.builder-input:hover {
  border-color: var(--color-primary-lighter);
}

.builder-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-bg);
}

/* Button Groups */
.button-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.group-btn {
  padding: 8px 14px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition-normal);
  font-family: inherit;
}

.group-btn:hover {
  border-color: var(--color-primary-lighter);
}

.group-btn.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.group-btn.mono {
  font-family: 'SF Mono', monospace;
  font-size: 1rem;
  padding: 8px 12px;
  min-width: 40px;
  text-align: center;
}

/* Severity buttons */
.group-btn.severity-info.active { background: var(--color-info); border-color: var(--color-info); }
.group-btn.severity-warning.active { background: var(--color-warning); border-color: var(--color-warning); }
.group-btn.severity-critical.active { background: var(--color-danger); border-color: var(--color-danger); }

/* Days Selector */
.days-selector {
  display: flex;
  gap: 6px;
}

.day-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition-normal);
}

.day-btn:hover {
  border-color: var(--color-primary-lighter);
}

.day-btn.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

/* Toggle */
.toggle-label {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
}

.toggle-label.compact {
  align-items: center;
}

.toggle-input {
  display: none;
}

.toggle-switch {
  width: 44px;
  height: 24px;
  background: var(--color-border);
  border-radius: 12px;
  position: relative;
  transition: var(--transition-normal);
  flex-shrink: 0;
}

.toggle-switch.small {
  width: 36px;
  height: 20px;
  border-radius: 10px;
}

.toggle-switch::after {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  top: 2px;
  left: 2px;
  transition: var(--transition-normal);
  box-shadow: var(--shadow-sm);
}

.toggle-switch.small::after {
  width: 16px;
  height: 16px;
}

.toggle-input:checked + .toggle-switch {
  background: var(--color-primary);
}

.toggle-input:checked + .toggle-switch::after {
  transform: translateX(20px);
}

.toggle-input:checked + .toggle-switch.small::after {
  transform: translateX(16px);
}

.toggle-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toggle-text strong {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-primary);
}

.toggle-text small {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}

/* ============================================
   OUTPUT SECTION
   ============================================ */
.output-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.output-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.output-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
}

.output-label .material-symbols-outlined {
  font-size: 16px;
}

.output-code {
  padding: 14px 16px;
  background: var(--color-surface-secondary);
  border-radius: var(--radius-sm);
  font-family: 'SF Mono', monospace;
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  overflow-x: auto;
  white-space: pre;
  margin: 0;
}

/* ============================================
   ACTIONS
   ============================================ */
.calculator-actions {
  display: flex;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border-light);
}

.actions-spacer {
  flex: 1;
}

/* ============================================
   BUTTONS
   ============================================ */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: var(--transition-normal);
  font-family: inherit;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn .material-symbols-outlined {
  font-size: 18px;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition-fast);
}

.btn-icon:hover {
  background: var(--color-surface-hover);
  color: var(--color-text-secondary);
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-light);
}

.btn-add {
  background: var(--color-primary);
  color: white;
  width: 100%;
}

.btn-add:hover:not(:disabled) {
  background: var(--color-primary-light);
}

.btn-ghost {
  background: transparent;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
}

.btn-ghost:hover:not(:disabled) {
  background: var(--color-surface-hover);
  border-color: var(--color-primary-lighter);
}

.btn-danger-ghost {
  background: transparent;
  color: var(--color-danger);
  border: 1px solid var(--color-danger);
}

.btn-danger-ghost:hover:not(:disabled) {
  background: var(--color-danger-bg);
}

/* ============================================
   MODAL
   ============================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
}

.modal-card {
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  width: 100%;
  max-width: 500px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--color-surface-secondary);
  border-bottom: 1px solid var(--color-border);
}

.modal-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.modal-body {
  padding: 20px;
}

.import-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-family: 'SF Mono', monospace;
  font-size: 0.8125rem;
  resize: vertical;
}

.import-error {
  margin-top: 10px;
  padding: 10px;
  background: var(--color-danger-bg);
  color: var(--color-danger);
  border-radius: var(--radius-sm);
  font-size: 0.8125rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  background: var(--color-surface-secondary);
  border-top: 1px solid var(--color-border);
}

/* ============================================
   TRANSITIONS
   ============================================ */
.slide-expand-enter-active,
.slide-expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-expand-enter,
.slide-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

/* ============================================
   MATERIAL SYMBOLS
   ============================================ */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-variation-settings:
      'FILL' 0,
      'wght' 400,
      'GRAD' 0,
      'opsz' 24;
}
</style>
